import { IOnChange, IPaginationParams, IPaginationResponse } from "interfaces/IPagination"
import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useDebounce } from "../useDebounce"

interface Props<T> {
  service: (x: IPaginationParams, s: AbortSignal) => Promise<IPaginationResponse<T>>
  fetchOnlyOnSearch?: boolean
  paramsDefault?: {
    [key: string]: string | number | null | boolean | string[]
  }
}

export type IStatusTable = "initial" | "pending" | "error" | "success"

export const useTableParams = <T>({
  service,
  paramsDefault,
  fetchOnlyOnSearch = false,
}: Props<T>) => {
  const controller = new AbortController()
  const [status, setStatus] = useState<IStatusTable>("pending")
  const [searchUrl, setSearchUrl] = useSearchParams()
  const [data, setData] = useState<T[]>([])
  const [params, setParams] = useState<IPaginationParams>({
    page: Number(searchUrl.get("page") || 1),
    pageSize: Number(searchUrl.get("pageSize") || 10),
    search: searchUrl.get("search") || undefined,
    ...paramsDefault,
  })
  const [count, setCount] = useState(0)
  const debaunceSearch = useDebounce({ fn: handleSearch, delay: 500 })
  const debaunceChange = useDebounce({ fn: onChange, delay: 200 })

  function handleSearch(e: ChangeEvent<HTMLInputElement>) {
    const search = e.target.value
    setParams(state => ({ ...state, page: 1, search }))
    setSearchUrl({ search })
  }

  function onChange(newParams: IOnChange) {
    setParams(state => ({ ...state, ...newParams }))
  }

  const run = useCallback(async () => {
    if (fetchOnlyOnSearch && !params.search) {
      setStatus("success")
      setData([])
      return
    }
    try {
      setStatus("pending")
      const { results, totalCount } = await service(
        {
          ...params,
          orderBy: (params?.orderDirection || paramsDefault?.orderBy) as string,
        },
        controller.signal,
      )

      setData(results)
      setCount(totalCount)
      setStatus("success")
    } catch {
      if (controller.signal.aborted === false) {
        setStatus("error")
      }
    }
  }, [service, params])

  useEffect(() => {
    run()
    return () => {
      controller.abort()
    }
  }, [params])

  return {
    data,
    setData,
    params,
    onChange: debaunceChange,
    status,
    count,
    refresh: run,
    handleSearch: debaunceSearch,
  }
}
